import type { WidgetRenderer } from "./types.ts";

// Mapping of variant names to their dynamic import functions
const VARIANT_IMPORTS: Record<
  string,
  () => Promise<{ [key: string]: WidgetRenderer }>
> = {
  default: () => import("./variants/v1.ts"),
  "rec-logic-big-audience80|v1": () => import("./variants/v1.ts"),
  "rec-logic-big-audience80|v2": () => import("./variants/v1.ts"),

  "rec-logic-2|v1": () => import("./variants/v2.ts"),
  "rec-logic-2|v2": () => import("./variants/v3.ts"),
  "rec-logic-2|v3": () => import("./variants/v4.ts"),

  "rec-top-placement|v1": () => import("./variants/v5.ts"),

  "recommendation-list|v1": () => import("./variants/v6.ts"),

  "article-bottom-recirc|control": () => import("./variants/v8.ts"),
  "gol-design-si|test": () => import("./variants/v8.ts"),

  // Ads between articles
  "condensed-infinite-scroll-2|go-deeper1": () => import("./variants/v9.ts"),
  // Ads in article after first paragraph
  "condensed-infinite-scroll-2|go-deeper2": () => import("./variants/v10.ts"),

  "finite-scroll-v1|condensed": () => import("./variants/v10.ts"),

  //condensed infinite scroll logic experiment
  "logic-condensed|control": () => import("./variants/v9.ts"),
  "logic-condensed|lastupdate": () => import("./variants/v11.ts"),
  "logic-condensed|crossprop": () => import("./variants/v12.ts"),

  //condensed infinite scroll logic experiment
  "condensed-scroll-all-ads|condensed": () => import("./variants/v10.ts"),

  // Condensed design w/ stories widget experiment
  "condensed-design|control": () => import("./variants/v9.ts"),
  "condensed-design|last-update": () => import("./variants/v14.ts"),
};

// Cache for loaded variants to avoid re-importing
const variantCache = new Map<string, WidgetRenderer>();

/**
 * Dynamically loads a variant renderer function
 * @param variantName - The name of the variant to load
 * @returns Promise that resolves to the variant renderer function
 */
export async function loadVariant(
  variantName: string
): Promise<WidgetRenderer> {
  // Check cache first
  if (variantCache.has(variantName)) {
    return variantCache.get(variantName)!;
  }

  // Get the import function for this variant
  const importFn = VARIANT_IMPORTS[variantName] || VARIANT_IMPORTS["default"];

  try {
    // Dynamically import the variant module
    const variantModule = await importFn();

    // Extract the render function - variants export as renderV1, renderV2, etc.
    const renderFunction = Object.values(variantModule)[0] as WidgetRenderer;

    if (!renderFunction) {
      console.warn(
        `[MM Recirculation Widget] No render function found in variant: ${variantName}`
      );
      // Fall back to default variant
      const defaultModule = await VARIANT_IMPORTS["default"]();
      const defaultRenderer = Object.values(defaultModule)[0] as WidgetRenderer;
      variantCache.set(variantName, defaultRenderer);
      return defaultRenderer;
    }

    // Cache the loaded variant
    variantCache.set(variantName, renderFunction);

    console.debug(`[MM Recirculation Widget] Loaded variant: ${variantName}`);
    return renderFunction;
  } catch (error) {
    console.error(
      `[MM Recirculation Widget] Failed to load variant: ${variantName}`,
      error
    );

    // Fall back to default variant
    try {
      const defaultModule = await VARIANT_IMPORTS["default"]();
      const defaultRenderer = Object.values(defaultModule)[0] as WidgetRenderer;
      variantCache.set(variantName, defaultRenderer);
      return defaultRenderer;
    } catch (fallbackError) {
      console.error(
        `[MM Recirculation Widget] Failed to load default variant`,
        fallbackError
      );
      throw fallbackError;
    }
  }
}

/**
 * Preloads a variant without caching it (useful for prefetching)
 * @param variantName - The name of the variant to preload
 */
export async function preloadVariant(variantName: string): Promise<void> {
  if (variantCache.has(variantName)) {
    return; // Already loaded
  }

  const importFn = VARIANT_IMPORTS[variantName] || VARIANT_IMPORTS["default"];
  try {
    await importFn();
    console.debug(
      `[MM Recirculation Widget] Preloaded variant: ${variantName}`
    );
  } catch (error) {
    console.warn(
      `[MM Recirculation Widget] Failed to preload variant: ${variantName}`,
      error
    );
  }
}

/**
 * Gets list of all available variant names
 * @returns Array of variant names
 */
export function getAvailableVariants(): string[] {
  return Object.keys(VARIANT_IMPORTS);
}
